import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Empty, Icon, Skeleton, Tooltip } from 'antd';

import Layout from '~/components/UI/Layout/Layout';
import { ModuleHeader } from '~/components/ModuleHeader';
import Search from '~/_shared/components/Search';
import { request } from '~/components/Instance';

import MonthDrawer from './MonthDrawer';
import { MONTH_HEADS, STATUS, cellStyle, monthLabel, statusOf } from './helpers';
import {
  CustomerCell,
  GridBox,
  GridCell,
  GridRow,
  Legend,
  Summary,
  SummaryPill
} from './styles';

// Extratos bancários que os clientes mandam pelo portal (CON-325): uma linha
// por cliente, de janeiro a dezembro. O clique abre o mês para conferir.
const ExtratoBancario = () => {
  const params = new URLSearchParams(window.location.search);
  const linkCustomer = params.get('cliente');
  const linkMonth = params.get('mes');
  const currentYear = new Date().getFullYear();

  const [year, setYear] = useState(linkMonth ? Number(linkMonth.slice(0, 4)) : currentYear);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [onlyToCheck, setOnlyToCheck] = useState(false);
  const [open, setOpen] = useState(linkCustomer && linkMonth ? { customerId: linkCustomer, competence: linkMonth } : null);

  const load = useCallback((showLoading) => {
    if (showLoading) setLoading(true);
    return request('get', 'extrato-bancario?year=' + year, {}, false)
      .then((response) => setData(response.data.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [year]);

  useEffect(() => {
    load(true);
  }, [load]);

  const customers = useMemo(() => {
    if (!data) return [];
    const term = search.trim().toLowerCase();
    return data.customers.filter((customer) => {
      if (term && (customer.name || '').toLowerCase().indexOf(term) === -1 && String(customer.cod || '').indexOf(term) === -1) {
        return false;
      }
      if (onlyToCheck) {
        return customer.months.some((month) => month.status === 'em_conferencia');
      }
      return true;
    });
  }, [data, search, onlyToCheck]);

  const renderGrid = () => {
    if (loading && !data) {
      return <Skeleton active paragraph={{ rows: 10 }} />;
    }

    if (!data || !data.task) {
      return (
        <Empty
          description={
            <span>
              A aba Extrato Bancário ainda não está ligada.
              <br />
              <Link to="/configuracoes/extrato-bancario">Ligar em Configurações › Extrato Bancário</Link>
            </span>
          }
        />
      );
    }

    if (customers.length === 0) {
      if (!onlyToCheck && !search.trim() && data.summary.customers && data.summary.customers.enabled === 0) {
        return (
          <Empty
            description={
              <span>
                Nenhum cliente está com a aba Extrato Bancário ligada.
                <br />
                Ligue na gaveta do cliente (aba Serviços) ou para todos em{' '}
                <Link to="/configuracoes/extrato-bancario">Configurações › Extrato Bancário</Link>.
              </span>
            }
          />
        );
      }
      return <Empty description={onlyToCheck ? 'Nenhum envio esperando conferência.' : 'Nenhum cliente encontrado.'} />;
    }

    return (
      <GridBox>
        <GridRow className="head">
          <CustomerCell>Cliente</CustomerCell>
          {MONTH_HEADS.map((head) => (
            <GridCell key={head}>{head}</GridCell>
          ))}
        </GridRow>
        {customers.map((customer) => (
          <GridRow key={customer.id}>
            <CustomerCell>
              <span className="name" title={customer.name}>{customer.name}</span>
              <span className="sub">{[customer.cod, customer.type_taxation].filter(Boolean).join(' · ')}</span>
            </CustomerCell>
            {customer.months.map((month) => {
              const look = statusOf(month.status);
              const isOpen = open && String(open.customerId) === String(customer.id) && open.competence === month.competence;
              const text = month.status === 'em_conferencia' ? month.unchecked_count + '↑' : look.short;
              if (month.status === 'fora') {
                return (
                  <GridCell key={month.competence}>
                    <Tooltip title={monthLabel(month.competence) + ': antes do início do cliente'}>
                      <div style={{ height: 34, borderRadius: 6, background: '#F0F0F0' }} />
                    </Tooltip>
                  </GridCell>
                );
              }
              return (
                <GridCell key={month.competence}>
                  <Tooltip title={monthLabel(month.competence) + ': ' + look.label + (month.files_count ? ' · ' + month.files_count + ' PDF' : '')}>
                    <button
                      type="button"
                      aria-label={customer.name + ', ' + monthLabel(month.competence) + ': ' + look.label}
                      style={cellStyle(month.status, isOpen)}
                      onClick={() => setOpen({ customerId: customer.id, competence: month.competence })}
                    >
                      {text}
                    </button>
                  </Tooltip>
                </GridCell>
              );
            })}
          </GridRow>
        ))}
      </GridBox>
    );
  };

  const summary = data ? data.summary : null;

  return (
    <Layout>
      <ModuleHeader
        breadcrumbs={['Tarefas', 'Extratos dos clientes']}
        title="Extratos dos clientes"
        search={
          <Search
            value={search}
            placeholder="Buscar cliente"
            onChange={(event) => setSearch(event.target.value)}
          />
        }
        center={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Button icon="left" aria-label="Ano anterior" onClick={() => setYear(year - 1)} />
            <span style={{ fontWeight: 600, minWidth: 48, textAlign: 'center' }}>{year}</span>
            <Button icon="right" aria-label="Próximo ano" onClick={() => setYear(year + 1)} disabled={year >= currentYear} />
          </div>
        }
        actions={
          <Link to="/configuracoes/extrato-bancario">
            <Button icon="setting">Aviso e lembrete</Button>
          </Link>
        }
      />

      {summary && data.task && (
        <Summary>
          <SummaryPill
            color="#1F5FAD"
            background={onlyToCheck ? '#EAF1FB' : '#fff'}
            border="#A9C5EC"
            onClick={() => setOnlyToCheck(!onlyToCheck)}
          >
            <Icon type="cloud-upload" /> {summary.to_check} {summary.to_check === 1 ? 'envio' : 'envios'} do portal para conferir
            {onlyToCheck ? ' (filtrando)' : ''}
          </SummaryPill>
          <SummaryPill color="#B42318" border="#F0A9A1">
            {summary.missing_last_month} sem {monthLabel(summary.last_month).split('/')[0].toLowerCase()}
          </SummaryPill>
          {summary.customers && (
            <SummaryPill color="#722ed1" border="#e6ccff">
              {summary.customers.enabled} de {summary.customers.active} clientes com a aba ligada
            </SummaryPill>
          )}
          <SummaryPill color="#595959">
            Status da tarefa “{data.task.name}” do Calendário
          </SummaryPill>
        </Summary>
      )}

      {renderGrid()}

      <Legend>
        {['entregue', 'nao_entregue', 'em_conferencia', 'sem_movimento', 'pendente', 'fora'].map((key) => (
          <span key={key}>
            <i
              className="swatch"
              style={{ background: STATUS[key].background, border: '1px solid ' + (STATUS[key].border || STATUS[key].background) }}
            />
            {STATUS[key].label}
          </span>
        ))}
      </Legend>

      <MonthDrawer
        customerId={open ? open.customerId : null}
        competence={open ? open.competence : null}
        onClose={() => setOpen(null)}
        onChanged={() => load(false)}
      />
    </Layout>
  );
};

export default ExtratoBancario;
